import { useRef } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { RichTextEditor, RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'
import {
  AtsCreateMessageTemplateDocument,
  AtsMessageTemplatesSelectorDocument,
} from 'generated/resources/graphql'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import Modal from 'modules/core/components/Modal'
import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AtsMessageVariablesSelector } from 'modules/ats/components/AtsMessageVariablesSelector'
import { Visibility } from 'modules/ats/lib/enums'

interface MessageTemplateCreateFormData {
  name: string
  content: string
}

interface Props {
  content: string
  onClose: () => void
}

export const CreateMessageTemplateModal = ({ content, onClose }: Props) => {
  const textEditorRef = useRef<EditorHandle>(null)
  const richTextEditorRef = useRef<RichTextEditorHandle>(null)
  const { canUseRichTextEditor, loading: richTextEditorLoading, editorConfig } = useRichTextEditor()

  const {
    company: { id: companyId },
    access: { id: currentAccessId },
  } = useCurrentSession()

  const [createAtsMessageTemplate, { loading: creatingMessageTemplate }] = useMutation(
    AtsCreateMessageTemplateDocument,
    {
      extractorFn: (data) => data.atsMutations.createAtsMessageTemplate,
      refetchQueries: [AtsMessageTemplatesSelectorDocument],
    }
  )

  const form = useForm<MessageTemplateCreateFormData>({
    defaultValues: {
      content: content || '',
      name: '',
    },
    validate(data) {
      const errors: FormErrors<MessageTemplateCreateFormData> = {}

      if (form.dirty && (!data.content || data.content === '')) {
        errors.content = i18n.t('common.empty')
      }

      if (form.dirty && (!data.name || data.name === '')) {
        errors.name = i18n.t('common.empty')
      }

      return errors
    },
    async onSubmit(values, { setErrors }) {
      const result = await createAtsMessageTemplate({
        variables: {
          authorId: currentAccessId,
          body: values.content,
          companyId: companyId,
          name: values.name,
          visibility: Visibility.Personal,
        },
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.manage_message_templates.create_modal.success_created_message'),
        })
        onClose()
      }

      return handled
    },
  })

  const addVariable = (variable: string) => {
    form.setFieldValue('content', `${form.values.content} ${variable}`)
    richTextEditorRef?.current?.setContent(`${form.values.content ?? ''} ${variable}`)
  }

  return (
    <Modal
      title={i18n.t('ats.manage_message_templates.create_modal.personal_template_title')}
      description={i18n.t('ats.manage_message_templates.create_modal.description')}
      illustration="document-edit"
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Button disabled={creatingMessageTemplate || !form.isValid} submit>
          {i18n.t('ats.manage_message_templates.create_modal.create_button')}
        </Button>
      }
    >
      <TextField
        label={i18n.t('ats.manage_message_templates.create_modal.name_label')}
        value={form.values.name}
        onChange={(name) => form.setFieldValue('name', name)}
        error={form.errors.name}
      />

      <Box gap="s4" paddingTop="s24">
        <Box flexDirection="row" justifyContent="spaceBetween">
          <Text.Normal color="grey1000" size="200" weight="medium">
            {i18n.t('ats.manage_message_templates.create_modal.message_label')}
          </Text.Normal>
          <AtsMessageVariablesSelector
            onSelect={(value) => addVariable(value)}
            showJobData
            showSenderData
          />
        </Box>
        <ErrorBoundary>
          {richTextEditorLoading ? (
            <RichTextEditor.Skeleton />
          ) : canUseRichTextEditor ? (
            <RichTextEditor
              ref={richTextEditorRef}
              {...editorConfig}
              title={i18n.t('ats.manage_message_templates.create_modal.message_label')}
              onChange={(data) => form.setFieldValue('content', data?.value)}
              placeholder={i18n.t('ats.manage_message_templates.create_modal.placeholder')}
              initialEditorState={{
                content: form.values.content ?? '',
              }}
            />
          ) : (
            <TextEditor
              ref={textEditorRef}
              onChange={(data) => form.setFieldValue('content', data)}
              placeholder=""
              initialEditorState={{
                data: form.values.content,
              }}
              height="s360"
            />
          )}
        </ErrorBoundary>
      </Box>
    </Modal>
  )
}
